import { getFormProps, useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import { LaptopIcon, MoonIcon, SunIcon } from '@radix-ui/react-icons'
import { Button } from '@radix-ui/themes'
import {
  type ActionFunctionArgs,
  data,
  redirect,
  useFetcher,
  useFetchers,
} from 'react-router'
import { ServerOnly } from 'remix-utils/server-only'
import { z } from 'zod'
import { DexRadixSize } from '#app/config/ui.config.js'
import { useBreakpoints } from '#app/hooks/breakpoints.hook.js'
import { useHints } from '#app/utils/client-hints.tsx'
import { useRequestInfo } from '#app/utils/request-info.ts'
import { setTheme, type Theme } from '#app/utils/theme.server.ts'

const ThemeFormSchema = z.object({
  theme: z.enum(['system', 'light', 'dark']),
  // this is useful for progressive enhancement
  redirectTo: z.string().optional(),
})

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const submission = parseWithZod(formData, {
    schema: ThemeFormSchema,
  })

  invariantResponse(submission.status === 'success', 'Invalid theme received')

  const { theme, redirectTo } = submission.value

  const responseInit = {
    headers: { 'set-cookie': setTheme(theme) },
  }
  if (redirectTo) {
    return redirect(redirectTo, responseInit)
  } else {
    return data({ result: submission.reply() }, responseInit)
  }
}

export function ThemeSwitch({
  userPreference,
}: {
  userPreference?: Theme | null
}) {
  const fetcher = useFetcher<typeof action>()
  const requestInfo = useRequestInfo()
  const { is3xl } = useBreakpoints()

  const [form] = useForm({
    id: 'theme-switch',
    lastResult: fetcher.data?.result,
  })

  const optimisticMode = useOptimisticThemeMode()
  const mode = optimisticMode ?? userPreference ?? 'system'
  const nextMode =
    mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system'
  const modeLabel = {
    light: (
      <>
        <SunIcon />
        <span className="sr-only">Light</span>
      </>
    ),
    dark: (
      <>
        <MoonIcon />
        <span className="sr-only">Dark</span>
      </>
    ),
    system: (
      <>
        <LaptopIcon />
        <span className="sr-only">System</span>
      </>
    ),
  }

  return (
    <fetcher.Form
      method="POST"
      {...getFormProps(form)}
      className="mb-0"
      action="/api/theme-switch"
    >
      <ServerOnly>
        {() => (
          <input type="hidden" name="redirectTo" value={requestInfo.path} />
        )}
      </ServerOnly>
      <input type="hidden" name="theme" value={nextMode} />
      <Button
        type="submit"
        size={is3xl ? DexRadixSize.DEFAULT : DexRadixSize.SMALL}
        variant="soft"
      >
        {modeLabel[mode]}
      </Button>
    </fetcher.Form>
  )
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
  const fetchers = useFetchers()
  const themeFetcher = fetchers.find(f => f.formAction === '/api/theme-switch')

  if (themeFetcher && themeFetcher.formData) {
    const submission = parseWithZod(themeFetcher.formData, {
      schema: ThemeFormSchema,
    })

    if (submission.status === 'success') {
      return submission.value.theme
    }
  }
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
  const hints = useHints()
  const requestInfo = useRequestInfo()
  const optimisticMode = useOptimisticThemeMode()
  if (optimisticMode) {
    return optimisticMode === 'system' ? hints.theme : optimisticMode
  }
  return requestInfo.userPrefs.theme ?? hints.theme
}
